import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import { App } from './App.tsx'
import { initSettingsStorage } from './services/settings'
import { loadSettings } from './services/settings'
import { initVolumeFromSettings } from './services/volume'
import { initStaminaStorage } from './services/stamina'
import { initializeLiff } from './services/liff'
import { resolveSupportedLocale } from './i18n/locales'
import {
  initAnalytics,
  markAnalyticsPlatformResolved,
  trackAppLaunch,
  trackLiffOutcome,
} from './services/analytics'

// 認証前の離脱（LIFF 失敗・SDK 未到達）も計測対象なので、React 描画や
// LIFF 初期化より前に起動を記録する。測定 ID 未設定のビルドでは完全に no-op。
initAnalytics()
trackAppLaunch()

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root element not found')

const root = createRoot(rootEl)

/**
 * ネイティブ環境では Preferences → localStorage のミラーが済んでから描画する。
 * Web では no-op のためほぼ即座に描画が始まる。
 * LIFF 環境では `initializeLiff()` 完了後に描画することで、
 * `getPlatformId()` が `line_mini` を正しく返せる状態にしてから App がマウントされる。
 *
 * 参考: `src/services/storage.ts` の hydrateStorage() 詳細コメント。
 */
void (async () => {
  document.documentElement.lang = resolveSupportedLocale(navigator.language)

  // 3 初期化を並列に待つ。どれも失敗しても「デフォルト値で起動」にフォールバックする設計なので
  // allSettled で失敗を許容する（個別の warning ログは各サービス側で出す）。
  await Promise.allSettled([
    // hydrate 完了後、保存済みのミュート状態を volume ストアに反映する。
    // 順序: hydrate → ストア初期化。逆順だとネイティブで旧値が一時的に使われる。
    initSettingsStorage()
      .then(() => {
        initVolumeFromSettings()
      })
      .catch((err) => {
        // fallback:ok — hydrate 失敗は loadSettings 側で DEFAULT_SETTINGS に復帰する。
        // volume ストアも初期値のままで起動する（ブラウザは無音 / ネイティブは音 ON。
        // 分岐の理由は services/volume.ts の FALLBACK_MUTED を参照）。
        console.warn('[main] initSettingsStorage failed:', err)
      }),
    initStaminaStorage().catch((err) => {
      // fallback:ok — hydrate 失敗は loadStamina 側でフル回復状態に復帰する。
      console.warn('[main] initStaminaStorage failed:', err)
    }),
    // initializeLiff() は自前で status を返して throw しない設計なので try/catch 不要だが、
    // 予期せぬ同期例外に備え Promise 化して allSettled で拾う。
    initializeLiff().then((result) => {
      // ここで初めて platform 判定（line_mini / web_* / android / ios）が確定する。
      markAnalyticsPlatformResolved()
      trackLiffOutcome(result)
      if (result.status === 'redirecting') {
        // liff.login() もしくは VITE_LIFF_URL へのリダイレクトが進行中。
        // React 描画してもすぐに離脱する。
        console.log('[main] LIFF redirect in progress, skipping render')
      } else if (result.status === 'unavailable') {
        // localhost 開発用の継続経路。本番到達時は sdk.ts が認証必須で throw するので
        // App の error 画面に回る。
        console.log(`[main] LIFF unavailable: ${result.error ?? ''}`)
      }
    }),
  ])

  document.documentElement.lang = loadSettings().locale

  root.render(
    <StrictMode>
      <App />
    </StrictMode>,
  )
})()
